import React, { useEffect, useState, useRef } from "react";
import { FiCalendar, FiDownload, FiPlus } from "react-icons/fi";
import rosterApi from "../../../api/roster/RosterApi";
import clsx from "clsx";
import DatePicker from "react-datepicker";

/* -------------------------------------------------------
 * Component
 * ----------------------------------------------------- */
const AddClosedDateModal = ({
  onClose,
  onOpenSpecial,
  onSubmit,
  mode = "add",
  initialData,
  onImportHolidays, // 👈 NEW
}) => {
  const year = new Date().getFullYear();
  // const holidays = getNZPublicHolidays(year);

  const [holidays, setHolidays] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchHolidays = async () => {
      const { response } = await rosterApi.getPublicHolidays(year);

      console.log({ response });
      if (response?.data?.success) {
        setHolidays(response.data.holidays);
      }
    };
    fetchHolidays();
  }, [year]);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setStartDate(initialData.startDate || initialData.date || "");
      setEndDate(initialData.endDate || initialData.date || "");
      setNote(initialData.note || "");
    }
  }, [mode, initialData]);

  // console.log({ holidays });
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleSelectHoliday = (holiday) => {
    if (!holiday || !holiday.date) return;
    setStartDate(holiday.date);
    setEndDate(holiday.date);
    setNote(`${holiday.label} ${year}`);
  };

  const handleSubmit = () => {
    if (!startDate) return;

    onSubmit?.({
      startDate,
      endDate: endDate || startDate,
      note,
    });

    onClose();
  };

  const handleImport = async () => {
    await onImportHolidays?.(year);
    onClose();
  };

  const formatDateDDMMMYYYY = (isoDate) => {
    if (!isoDate) return "";
    const d = new Date(isoDate);

    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const toISODate = (date) => {
    if (!date) return "";

    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");

    return `${y}-${m}-${d}`;
  };

  const fromISODate = (iso) => (iso ? new Date(iso + "T00:00:00") : null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative bg-dry border border-border rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-yellowlight">
          {mode === "edit" ? "Edit closed date" : "Add closed date"}
        </h2>

        <div className="space-y-4 relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={clsx(
              "w-full border rounded-md px-3 py-2 bg-dry border-border text-sm text-left",
              selected === "__OTHER__" ? "text-yellowlight" : "text-textGray"
            )}
          >
            {selected ? selected.label : `Select NZ public holiday (${year})`}
          </button>

          {open && (
            <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-dry shadow-xl">
              <div className="max-h-[200px] overflow-y-auto scrollbar-thin">
                {/* Placeholder */}
                <div
                  className="px-3 py-2 text-sm text-textGray hover:bg-white/5 cursor-pointer"
                  onClick={() => {
                    setSelected(null);
                    setOpen(false);
                  }}
                >
                  Select NZ public holiday ({year})
                </div>

                {/* Holidays */}
                {holidays.map((h) => (
                  <div
                    key={h.label}
                    onClick={() => {
                      setSelected({
                        label: `${h.label}. ${formatDateDDMMMYYYY(h.date)}`,
                      });
                      handleSelectHoliday(h);
                      setOpen(false);
                    }}
                    className="px-3 py-2 text-sm text-textGray hover:bg-white/5 cursor-pointer"
                  >
                    {h.label}
                    {!h.date
                      ? " (date announced yearly)"
                      : `. ${formatDateDDMMMYYYY(h.date)}`}
                  </div>
                ))}

                {/* Divider */}
              </div>

              <div className="my-1 border-t border-border" />

              {/* Other */}
              <div
                onClick={() => {
                  setSelected("__OTHER__");
                  onOpenSpecial();
                  setOpen(false);
                }}
                className="px-3 py-2 text-sm text-yellowlight hover:bg-yellowlight/10 cursor-pointer"
              >
                Other (special date)
              </div>
            </div>
          )}
          <div className="flex flex-col space-y-4">
            {" "}
            <DatePicker
              selected={fromISODate(startDate)}
              onChange={(date) => {
                const iso = toISODate(date);
                setStartDate(iso);

                // auto sync end date if empty or earlier
                if (!endDate || endDate < iso) {
                  setEndDate(iso);
                }
              }}
              dateFormat="dd-MMM-yyyy"
              placeholderText="Start date"
              className="w-full border rounded-md px-3 py-2 bg-dry border-border text-sm text-textGray"
              calendarClassName="bg-dry border border-border text-textGray"
              popperPlacement="bottom-start"
            />
            <DatePicker
              selected={fromISODate(endDate)}
              onChange={(date) => {
                setEndDate(toISODate(date));
              }}
              dateFormat="dd-MMM-yyyy"
              placeholderText="End date"
              minDate={fromISODate(startDate)}
              className="w-full border rounded-md px-3 py-2 bg-dry border-border text-sm text-textGray"
              calendarClassName="bg-dry border border-border text-textGray"
              popperPlacement="bottom-start"
            />
          </div>

          {/* <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full border rounded-md px-3 py-2 bg-dry border-border text-sm text-textGray"
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full border rounded-md px-3 py-2 bg-dry border-border text-sm text-textGray"
          /> */}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="border px-4 py-2 rounded-md bg-dry border-border text-sm text-textGray"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="bg-yellowlight text-yellowdark hover:bg-yellowdark hover:text-yellowlight px-4 py-2 rounded-md text-sm"
          >
            {mode === "edit" ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddClosedDateModal;
